import { Fade, useTheme } from '@mui/material';
import { UrlSegments } from '@provision/portal-routes';
import { useCallback, useMemo, useState } from 'react';
import { MetaTags } from 'src/components';
import { Project } from 'src/graphql';
import { EntityNames } from 'src/graphql/types';
import { EntityWindowWrapper } from 'src/pages/Conversations/shared-layout';
import { BaseSelectedView } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import { CreateButton } from 'src/pages/CustomViews/custom-views-selected-view/components/create-button';
import { useSelectedEntityParams } from 'src/pages/CustomViews/custom-views-selected-view/hooks/use-selected-entity-params';
import { ProjectSidePanel } from 'src/pages/Projects/SidePanel/side-panel';
import { useOnEsc } from 'src/utils/hooks';

export const ProjectSelectedView = () => {
  const [selectedRecord, setSelectedRecord] = useState<Project | undefined>();

  const {
    record: project,
    recordId: projectId,
    openRecord,
    closeRecord,
    customView,
    viewId,
    selectedCustomViewId,
  } = useSelectedEntityParams({ entityName: EntityNames.Project });

  const selectedProject = useMemo(
    () => (projectId ? ((project as Project) ?? selectedRecord) : undefined),
    [projectId, project, selectedRecord],
  );

  const theme = useTheme();

  const onPanelClose = useCallback(() => {
    closeRecord();
    setSelectedRecord(undefined);
  }, [closeRecord]);

  useOnEsc(onPanelClose);

  return (
    <MetaTags title={selectedProject?.address?.fullAddress || customView?.name || 'Projects'}>
      <BaseSelectedView<Project>
        title={selectedCustomViewId === UrlSegments.view.all ? 'All projects' : ''}
        entity={EntityNames.Project}
        viewId={viewId}
        selectedRecord={selectedProject}
        onRecordClick={(projectId, project) => {
          openRecord(projectId);
          setSelectedRecord(project);
        }}
        onRecordClose={() => {
          closeRecord();
          setSelectedRecord(undefined);
        }}
        leftPanelWidth={5000}
        sx={{
          [theme.breakpoints.up(0)]: {
            maxWidth: '100%',
          },
        }}
        slots={{
          rightPanel: !!selectedProject && (
            <Fade in={!!selectedProject}>
              <EntityWindowWrapper
                sx={{
                  [theme.breakpoints.up('sm')]: {
                    minWidth: '340px',
                  },
                }}
              >
                <ProjectSidePanel onClose={onPanelClose} project={selectedProject} />
              </EntityWindowWrapper>
            </Fade>
          ),
          createButton: <CreateButton icon="add_home_work" />,
        }}
      />
    </MetaTags>
  );
};
